import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Search, Loader2, CheckCircle, XCircle, ArrowRight, Globe, Mail,
  Phone, MapPin, Building2, Briefcase, BarChart3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function SeoAuditPage() {
  usePageTitle("Audit SEO gratuit");
  const [step, setStep] = useState("form"); // form | loading | result
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", website_url: "", city: "", activity: "" });
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.website_url) {
      toast.error("Nom, email et URL du site requis");
      return;
    }
    setStep("loading");
    try {
      const res = await axios.post(`${API}/public/seo-audit`, form);
      setResult(res.data.audit);
      setStep("result");
    } catch {
      toast.error("Erreur lors de l'audit");
      setStep("form");
    }
  };

  const scoreColor = (s) => s >= 70 ? "#10B981" : s >= 40 ? "#F59E0B" : "#EF4444";
  const scoreLabel = (s) => s >= 70 ? "Bon" : s >= 40 ? "A ameliorer" : "Critique";

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0099CC]/5 via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-12 relative text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 text-[#10B981] text-xs font-semibold mb-6">
            <Search className="h-3.5 w-3.5" /> Audit SEO gratuit
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-[1.1] mb-4">
            Analysez votre site en{" "}
            <span className="text-[#0099CC]">30 secondes</span>
          </h1>
          <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Obtenez un diagnostic SEO complet et gratuit. Identifiez les points a ameliorer pour booster votre visibilite en ligne.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-20">
        {step === "form" && (
          <form onSubmit={submit} className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 space-y-6" data-testid="seo-audit-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Nom</Label>
                <Input value={form.name} onChange={e => set("name", e.target.value)} required
                  placeholder="Votre nom complet" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-name" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" />Entreprise</Label>
                <Input value={form.company} onChange={e => set("company", e.target.value)}
                  placeholder="Nom de votre entreprise" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-company" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Email</Label>
                <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} required
                  placeholder="email@exemple.com" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Telephone</Label>
                <Input value={form.phone} onChange={e => set("phone", e.target.value)}
                  placeholder="06 XX XX XX XX" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-phone" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" />URL du site web</Label>
                <Input value={form.website_url} onChange={e => set("website_url", e.target.value)} required
                  placeholder="https://www.monsite.fr" className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-url" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Ville</Label>
                <Input value={form.city} onChange={e => set("city", e.target.value)}
                  placeholder="Paris, Lyon, etc." className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-city" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" />Activite</Label>
                <Input value={form.activity} onChange={e => set("activity", e.target.value)}
                  placeholder="Ex: Plomberie, Restaurant, Agence..." className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="audit-activity" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#0099CC] hover:bg-[#0088B5] text-white py-6 text-lg font-semibold rounded-xl" data-testid="audit-submit-btn">
              <Search className="h-5 w-5 mr-2" />Lancer l'audit SEO gratuit
            </Button>
            <p className="text-xs text-[#64748b] text-center">Votre rapport sera envoye par email. Vos donnees sont protegees.</p>
          </form>
        )}

        {step === "loading" && (
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-16 text-center" data-testid="audit-loading">
            <Loader2 className="h-12 w-12 animate-spin text-[#0099CC] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Analyse en cours...</h3>
            <p className="text-[#94A3B8]">Nous analysons {form.website_url}</p>
          </div>
        )}

        {step === "result" && result && (
          <div className="space-y-6" data-testid="audit-result">
            {/* Score */}
            <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-4"
                style={{ backgroundColor: `${scoreColor(result.score)}15`, border: `3px solid ${scoreColor(result.score)}` }}>
                <span className="text-3xl font-bold" style={{ color: scoreColor(result.score) }}>{result.score}</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">Score SEO : {result.score}/100</h2>
              <p className="text-lg" style={{ color: scoreColor(result.score) }}>{scoreLabel(result.score)}</p>
              <p className="text-[#64748b] text-sm mt-2">{result.url}</p>
            </div>

            {/* Checks */}
            <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#0099CC]" />Diagnostic detaille
              </h3>
              <div className="space-y-2">
                {result.checks?.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-[#2A3441] last:border-0" data-testid={`check-${i}`}>
                    <div className="flex items-center gap-3">
                      {c.passed ? <CheckCircle className="h-5 w-5 text-[#10B981]" /> : <XCircle className="h-5 w-5 text-[#EF4444]" />}
                      <span className="text-white text-sm font-medium">{c.name}</span>
                    </div>
                    <span className={`text-sm ${c.passed ? "text-[#10B981]" : "text-[#EF4444]"}`}>{c.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-3">Recommandations</h3>
                <ul className="space-y-2">
                  {result.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                      <ArrowRight className="h-4 w-4 text-[#0099CC] shrink-0 mt-0.5" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-8 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Ameliorez votre score SEO</h3>
              <p className="text-[#94A3B8] mb-6 max-w-lg mx-auto">
                Nos experts peuvent vous aider a optimiser votre site et atteindre un score de 90+.
                Demandez un accompagnement personnalise.
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Link to="/devis" className="inline-flex items-center gap-2 bg-[#0099CC] hover:bg-[#0088B5] text-white px-6 py-3 rounded-xl font-semibold transition-all" data-testid="audit-cta-demo">
                  Demander un accompagnement <ArrowRight className="h-4 w-4" />
                </Link>
                <Button variant="outline" onClick={() => { setStep("form"); setResult(null); }}
                  className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid="audit-retry-btn">
                  Analyser un autre site
                </Button>
              </div>
            </div>

            <p className="text-xs text-[#64748b] text-center">
              Un rapport detaille a ete envoye a {form.email}. Un expert vous contactera prochainement.
            </p>
          </div>
        )}
      </section>

      {/* Trust section */}
      <section className="bg-[#080C14] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Pourquoi choisir GLOTECH OS ?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {[
              { title: "Audit gratuit", desc: "Diagnostic SEO complet en 30 secondes, sans engagement" },
              { title: "Experts certifies", desc: "Notre equipe vous accompagne pour atteindre vos objectifs" },
              { title: "Resultats mesurables", desc: "Suivi en temps reel de vos performances avec notre plateforme" },
            ].map(t => (
              <div key={t.title} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
                <CheckCircle className="h-8 w-8 text-[#10B981] mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-1">{t.title}</h3>
                <p className="text-[#64748b] text-sm">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
